import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AsyncBoundary, Icon } from '@kollez/design-system';
import { useDrives } from '@/lib/queries';
import { formatIndianDate, formatLPA } from '@kollez/shared';

/**
 * /group/drives (M2 from Step 90 ledger) — cross-college drives roll-up for a Group /
 * University tenant. Same `useDrives()` hook a TPO uses; the tenantIsolation middleware
 * scopes to the caller's tenant, and for a group_admin's tenant that includes drives at
 * every college under the group.
 *
 * Read-only — Group admin doesn't post drives (that's TPO's job on the college side).
 * Drive detail link jumps to /tpo/drives/:id since the workspace surface is TPO-shaped.
 */

type Seg = 'live' | 'upcoming' | 'done';

const SEG_TO_STATUS: Record<Seg, string[]> = {
  live: ['open', 'assessing', 'interviewing', 'offers'],
  upcoming: ['draft'],
  done: ['closed', 'cancelled'],
};

function statusBadge(status: string): { label: string; tone: string } {
  const s = status.replace(/_/g, ' ');
  if (/offer/i.test(s)) return { label: s, tone: 'badge-success' };
  if (/interview|assessing/i.test(s)) return { label: s, tone: 'badge-info' };
  if (/draft/i.test(s)) return { label: s, tone: 'badge-warning' };
  return { label: s, tone: '' };
}

export function GroupDrivesPage() {
  const { data, isLoading, error, refetch } = useDrives();
  const [seg, setSeg] = useState<Seg>('live');
  const [q, setQ] = useState('');
  const [college, setCollege] = useState('');

  const rows = data?.drives ?? [];

  // Unique colleges present in the roll-up — populated dropdown lets group admin narrow to one.
  const colleges = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of rows) {
      const c = (d as { college?: { _id?: string; name?: string } }).college;
      if (c?._id && c.name) map.set(String(c._id), c.name);
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((d) => {
      const okSeg = SEG_TO_STATUS[seg].some((s) => d.status?.startsWith(s));
      const partnerName = (d.partner as { name?: string })?.name ?? '';
      const oppTitle = (d.opportunity as { title?: string })?.title ?? '';
      const collegeName = (d as { college?: { name?: string } }).college?.name ?? '';
      const okQ = !ql || `${partnerName} ${d.driveName} ${oppTitle} ${collegeName}`.toLowerCase().includes(ql);
      const dCollege = String((d as { college?: { _id?: string } }).college?._id ?? '');
      const okCollege = college === '' || dCollege === college;
      return okSeg && okQ && okCollege;
    });
  }, [rows, seg, q, college]);

  const segCounts = useMemo(() => {
    const counts = { live: 0, upcoming: 0, done: 0 };
    for (const d of rows) {
      if (SEG_TO_STATUS.live.some((s) => d.status?.startsWith(s))) counts.live++;
      else if (SEG_TO_STATUS.upcoming.some((s) => d.status?.startsWith(s))) counts.upcoming++;
      else if (SEG_TO_STATUS.done.some((s) => d.status?.startsWith(s))) counts.done++;
    }
    return counts;
  }, [rows]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Drives across your colleges</h1>
          <p className="subhead">
            Every company visit at every college in your group, newest first. Drill into a drive to
            see its Kanban + offers on the college's own placement page.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <div className="filterbar-search">
            <Icon name="search" size={14} className="text-ink-tertiary" />
            <input
              className="input"
              type="search"
              placeholder="Search company, drive name, opportunity, college"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="segmented">
            {(['live', 'upcoming', 'done'] as Seg[]).map((s) => (
              <button key={s} aria-pressed={seg === s} onClick={() => setSeg(s)}>
                {s === 'live' ? 'Running' : s === 'upcoming' ? 'Upcoming' : 'Finished'}
                <span className="helper ml-1">{segCounts[s]}</span>
              </button>
            ))}
          </div>
          {colleges.length > 1 && (
            <select className="select" value={college} onChange={(e) => setCollege(e.target.value)}>
              <option value="">All colleges</option>
              {colleges.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          )}
        </div>

        <AsyncBoundary
          kind="table"
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle={seg === 'live' ? 'Nothing running right now' : seg === 'upcoming' ? 'No upcoming drives' : 'No finished drives yet'}
          emptyHint={seg === 'live' ? 'When any college in your group starts a drive, it lands here live.' : 'Try a different segment or clear filters.'}
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Drive</th>
                  <th className="hide-mobile">College</th>
                  <th className="hide-mobile">Date</th>
                  <th>CTC</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((d) => {
                  const college = (d as { college?: { _id?: string; name?: string; code?: string } }).college;
                  const partner = (d as { partner?: { name?: string } }).partner;
                  const opp = (d as { opportunity?: { title?: string; compensation?: { ctc?: { min?: number } } } }).opportunity;
                  const badge = statusBadge(d.status);
                  const ctc = opp?.compensation?.ctc?.min;
                  return (
                    <tr key={d._id}>
                      <td>
                        <div className="cell-title">
                          <Link className="link" to={`/tpo/drives/${d._id}`}>{d.driveName}</Link>
                        </div>
                        <div className="cell-sub">{partner?.name ?? '—'} · {opp?.title ?? '—'}</div>
                      </td>
                      <td className="hide-mobile cell-sub">{college?.name ?? '—'}</td>
                      <td className="hide-mobile cell-sub">{d.driveDate ? formatIndianDate(d.driveDate) : '—'}</td>
                      <td className="cell-sub">{typeof ctc === 'number' ? formatLPA(ctc) : '—'}</td>
                      <td><span className={`badge ${badge.tone}`}>{badge.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <p className="helper">
        Group admins get read-only visibility. To post a drive or manage a Kanban, use the college's
        placement office view — click a drive above to jump in.
      </p>
    </div>
  );
}
